import { XGridDefaultColumn } from '@AMIEWEB/Common/XGrid/cells/XGridDefaults';
import moment from 'moment';
import { ErrorTooltipStartTime } from '../../CommonComponents/EditComponents/ErrorTooltipStartTime';
import { EditStartTimeCell } from '../../CommonComponents/EditComponents/EditStartTime';
import { GridPreProcessEditCellProps } from '@mui/x-data-grid';
import { isInValidAmount, isInValidDdlItem, isInValidTime } from '../../CommonComponents/Utils';
import { missingField } from 'app/constants';
import { ErrorTooltipEndTime } from '../../CommonComponents/EditComponents/ErrorTooltipEndTime';
import { EditEndTimeCell } from '../../CommonComponents/EditComponents/EditEndTime';
import { ErrorTooltipAmount } from '../../CommonComponents/EditComponents/ErrorTooltipAmount';
import { EditAmountCell } from '../../CommonComponents/EditComponents/EditAmount';
import { EditTypeCell } from '../../CommonComponents/EditComponents/EditType';
import { ErrorTooltipType } from '../../CommonComponents/EditComponents/ErrorTooltipType';
import { EditDescriptionTypeCell } from '../../CommonComponents/EditComponents/EditDescriptionType';
import { ErrorTooltipDescriptionType } from '../../CommonComponents/EditComponents/ErrorTooltipDescriptionType';
import { SelectAll } from '../../CommonComponents/SelectAll';
import { EditSelectAllCell } from '../../CommonComponents/EditComponents/EditSelectAll';
import { ShiftDiffWeekday } from '../../CommonComponents/Weekday';
import { EditWeekdayCell } from '../../CommonComponents/EditComponents/EditWeekday';
import { modsEnums } from 'app/models/Placement/PlacementMods';
import { xGridDateTimeSort } from '@AMIEWEB/Common/XGrid/sorting/XGridSorting';

export const defaultBillingShiftColumn = {
  ...XGridDefaultColumn,
  flex: 0,
};

export const getBillingShiftDiffGridColumns = (t, isModEdit, classes, setErrors) => {
  return [
    {
      ...defaultBillingShiftColumn,
      field: 'startTime',
      headerName: isModEdit ? `${t('placement.placementMods.startTime')}*` : t('placement.placementMods.startTime'),
      width: 141,
      hide: false,
      renderCell: ErrorTooltipStartTime,
      valueGetter: params => {
        return params.row.startTime ? ` ${moment(params.row.startTime).format('hh:mm a')}` : missingField;
      },
      renderEditCell: params =>
        EditStartTimeCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        if (isInValidTime(params.row?.startTime)) {
          return { ...params.props.row, error: true, errorField: 'startTime' };
        } else {
          return { ...params.props, error: false, errorField: null };
        }
      },
      cellClassName: params => {
        if (isInValidTime(params.row?.startTime)) {
          return classes.isErrorCell;
        } else {
          return null;
        }
      },
      editable: isModEdit,
      hideable: !isModEdit,
      sortComparator: xGridDateTimeSort('startTime'),
    },
    {
      ...defaultBillingShiftColumn,
      field: 'endTime',
      headerName: isModEdit ? `${t('placement.placementMods.endTime')}*` : t('placement.placementMods.endTime'),
      renderCell: ErrorTooltipEndTime,
      valueGetter: params => {
        return params.row.endTime ? ` ${moment(params.row.endTime).format('hh:mm a')}` : missingField;
      },
      width: 141,
      hide: false,
      renderEditCell: params => EditEndTimeCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        if (isInValidTime(params.row?.endTime)) {
          return { ...params.props.row, error: true, errorField: 'endTime' };
        } else {
          setErrors(prevErrors => ({
            ...prevErrors,
            id: params.id,
          }));
          return { ...params.props, error: false, errorField: null };
        }
      },
      cellClassName: params => {
        if (isInValidTime(params.row?.endTime)) {
          return classes.isErrorCell;
        } else {
          return null;
        }
      },
      editable: isModEdit,
      hideable: !isModEdit,
      sortComparator: xGridDateTimeSort('endTime'),
    },
    {
      ...defaultBillingShiftColumn,
      headerName: isModEdit ? `${t('placement.placementMods.amount')}*` : t('placement.placementMods.amount'),
      field: 'amount',
      width: 83,
      hide: false,
      editable: isModEdit,
      renderCell: ErrorTooltipAmount,
      renderEditCell: params => EditAmountCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        if (isInValidAmount(params.row.amount)) {
          return { ...params.props.row, error: true, errorField: 'amount' };
        } else {
          return { ...params.props, error: false, errorField: null };
        }
      },
      cellClassName: params => {
        if (isInValidAmount(params.row.amount)) {
          return classes.isErrorCell;
        } else {
          return null;
        }
      },
      align: 'right',
    },
    {
      ...defaultBillingShiftColumn,
      headerName: isModEdit ? `${t('placement.placementMods.type')}*` : t('placement.placementMods.type'),
      field: 'shiftDifferentialType',
      renderEditCell: params => EditTypeCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      renderCell: ErrorTooltipType,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        if (isInValidDdlItem(params.row.shiftDifferentialType)) {
          return { ...params.props.row, error: true, errorField: 'shiftDifferentialType' };
        } else {
          setErrors(prevErrors => ({
            ...prevErrors,
            id: params.id,
          }));
          return { ...params.props, error: false, errorField: null };
        }
      },
      cellClassName: params => {
        if (isInValidDdlItem(params.row.shiftDifferentialType)) {
          return classes.isErrorCell;
        } else {
          return null;
        }
      },
      width: 121,
      hide: false,
      editable: isModEdit,
    },
    {
      ...defaultBillingShiftColumn,
      headerName: isModEdit
        ? `${t('placement.placementMods.descriptionType')}*`
        : t('placement.placementMods.descriptionType'),
      field: 'shiftDifferentialDescriptionType',
      renderEditCell: params =>
        EditDescriptionTypeCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      renderCell: ErrorTooltipDescriptionType,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
        if (isInValidDdlItem(params.row.shiftDifferentialDescriptionType)) {
          return { ...params.props.row, error: true, errorField: 'shiftDifferentialDescriptionType' };
        } else {
          setErrors(prevErrors => ({
            ...prevErrors,
            id: params.id,
          }));
          return { ...params.props, error: false, errorField: null };
        }
      },
      cellClassName: params => {
        if (isInValidDdlItem(params.row.shiftDifferentialDescriptionType)) {
          return classes.isErrorCell;
        } else {
          return null;
        }
      },
      width: 138,
      hide: false,
      editable: isModEdit,
    },
    {
      field: 'selectAll',
      headerName: t('placement.placementMods.selectAll'),
      headerAlign: 'center',
      width: 100,
      hide: false,
      renderCell: SelectAll,
      renderEditCell: params =>
        EditSelectAllCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'sunday',
      headerName: 'Su',
      headerAlign: 'center',
      day: 'sunday',
      width: 40,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'monday',
      headerName: 'M',
      headerAlign: 'center',
      width: 40,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'tuesday',
      headerName: 'T',
      headerAlign: 'center',
      width: 36,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'wednesday',
      headerName: 'W',
      headerAlign: 'center',
      width: 40,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'thursday',
      headerName: 'Th',
      headerAlign: 'center',
      width: 40,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'friday',
      headerName: 'F',
      headerAlign: 'center',
      width: 40,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
    {
      field: 'saturday',
      headerName: 'S',
      headerAlign: 'center',
      width: 40,
      renderCell: ShiftDiffWeekday,
      renderEditCell: params => EditWeekdayCell({ cellParams: params, currentMod: modsEnums.BillingShiftDifferential }),
      editable: isModEdit,
    },
  ];
};
